import { useState } from 'react';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/api/queryClient';
import { fetchChannelAccounts, type ChannelAccount } from '@/api/channelAccounts';

// Same look as the modal's other fields (EditStudioModal's inputCls / labelCls).
const inputCls =
  'mt-1 block w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream';
const labelCls = 'block text-[10px] font-inter uppercase tracking-widest text-cream-soft';

export const CHANNEL_ACCOUNTS_QUERY_KEY = ['channel-accounts'] as const;

interface Props {
  studioId: string;
  /** Studio id -> name, to label an account held by another studio. */
  studioNames: Record<string, string>;
  /** The operator picked an account (the parent decides on Save whether it changed). */
  onChange: (account: ChannelAccount) => void;
}

export const accountHandleLabel = (a: ChannelAccount): string =>
  a.handle ? `@${a.handle}` : `Instagram account ····${a.accountExternalId.slice(-4)}`;

function Field({ studioId, studioNames, onChange }: Props) {
  const { data, isError, isPending } = useQuery({
    queryKey: CHANNEL_ACCOUNTS_QUERY_KEY,
    queryFn: fetchChannelAccounts,
    retry: false,
  });
  const [picked, setPicked] = useState<string | null>(null);

  if (isError && !data) {
    return (
      <div className={labelCls}>
        Instagram account
        <p className="mt-1.5 text-xs normal-case tracking-normal text-cream-faint">Couldn't load Instagram accounts</p>
      </div>
    );
  }
  const accounts = data ?? [];
  const current = accounts.find((a) => a.studioId === studioId);
  const value = picked ?? current?.accountExternalId ?? '';

  return (
    <label className={labelCls}>
      Instagram account
      <select
        className={inputCls}
        value={value}
        disabled={isPending}
        onChange={(e) => {
          const next = accounts.find((a) => a.accountExternalId === e.target.value);
          if (!next) return;
          setPicked(next.accountExternalId);
          onChange(next);
        }}
        data-testid="studio-edit-instagram"
      >
        {value === '' ? (
          <option value="" disabled>
            {isPending ? 'Loading…' : 'Not assigned'}
          </option>
        ) : null}
        {accounts.map((a) => (
          <option key={a.accountExternalId} value={a.accountExternalId}>
            {accountHandleLabel(a)}
            {a.studioId && !(a.studioId === studioId && a.accountExternalId === value)
              ? ` (${studioNames[a.studioId] ?? a.studioId})`
              : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The studio edit form's Instagram account dropdown (tv-isee.2, owner/georgina
 * only; the parent gates it). It lists the Instagram accounts linked in HookChat
 * by handle, starting on the first this studio holds (server order). An account
 * belongs to some studio by design, and a studio can hold more than one, so
 * EVERY option with a studioId is labelled with that studio's name — including
 * this studio's own other accounts — except the one currently shown as selected
 * when it belongs to this studio. There is no "remove" option: "Not assigned"
 * only shows until an account is picked. Carries its own provider (same shared
 * client as the root's), like SlotDepositPane, so it renders in isolation too.
 */
export default function StudioInstagramAccountField(props: Props) {
  return (
    <QueryClientProvider client={queryClient}>
      <Field {...props} />
    </QueryClientProvider>
  );
}
